import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader } from 'reactstrap';
import { Document } from "shared/models/site/document"
import { getDocument, getDocumentHistory } from "shared/services/railDocumentsApi";
import { DocumentHistory } from 'shared/models/api/models';
import { DocumentInfoH2, ModalClose } from "components/menu/Labels";
import { documentMapper } from "shared/models/mapping/mapping";
import { EditForm } from 'components/portal/documents/admin/AddEditDocument';
import { DocumentDetails } from "./DocumentDetails"
import { DocumentHistoryView } from "./DocumentHistoryView"
import 'react-bootstrap-typeahead/css/Typeahead.css';
import './DocumentInfo.css'
import { getUserExistingRulebookMode } from '../../../../shared/store/applicationState';

enum Tabs {
    TabDetails,
    TabHistory
}

const ModalSubHeader = (props: { children?: React.ReactNode }) => {
    return (
        <div className="modal-sub-header"><h3 className="modal-sub-header-bg">{props.children}</h3></div>
    );
}

type IDocumentFormProps = {
    isOpen: boolean,
    onClose: () => void,
    editDocument: (docId: number) => void,
    documentId: number,
    refresh: object,
    tabType: Tabs
}

const DocumentForm = (props: IDocumentFormProps) => {
    const [stateDocument, setStateDocument] = useState({} as Document);
    const [stateDocumentHistory, setStateDocumentHistory] = useState({} as DocumentHistory[]);
    const [infoTab, setInfoTab] = useState(props.tabType);
    const [isOpen, setIsOpen] = useState(false);

    const documentId = useRef(-1);

    const closeModal = () => {
        documentId.current = -1;
        setStateDocument({});
        setInfoTab(Tabs.TabDetails);
        setIsOpen(false);
        if (props.onClose) props.onClose();
    };

    const documentHistoryUpdated = async (docId: number) => {
        setInfoTab(Tabs.TabHistory);
        await getDocumentAndHistory(docId);
    }

    const getDocumentAndHistory = async (docId: number) => {
        if (docId) {
            const currentMode = getUserExistingRulebookMode();
            getDocument(docId, currentMode!)
                .then(railDoc => {
                    documentId.current = docId;
                    var doc = documentMapper.mapFromRailDocument(railDoc);
                    setStateDocument(doc);
                    setIsOpen(true);
                });

            getDocumentHistory(docId)
                .then(docHistory => {
                    setStateDocumentHistory(docHistory);
                });
        }
    }

    useEffect(() => {
        if (!props.isOpen) return;

        const mount = async () => {
            if (props.documentId) {
                await getDocumentAndHistory(props.documentId);
            }
        };

        mount();
    },
        [props.documentId, props.tabType, props.isOpen, props.refresh]);

    const ViewInfo = () => {
        const [tab, setTab] = useState(infoTab);

        const setTabView = (tab: Tabs) => {
            setTab(tab);
        }

        return (
            <Modal
                isOpen={isOpen}
                className="document-info-modal">
                <ModalHeader>
                    <DocumentInfoH2 />
                    <ModalClose onClick={closeModal} />
                </ModalHeader>
                <React.Fragment>
                    <ModalSubHeader>
                        <div style={{ display: "flex", alignContent: "flex-start" }} >
                            <div className="tab-header tab-header-spacer"> | </div>
                            <div style={{ cursor: 'pointer' }}
                                className={tab === Tabs.TabDetails ? 'tab-header tab-header-text-select selected' : 'tab-header tab-header-text'}
                                onClick={() => setTabView(Tabs.TabDetails)}>Document details</div>
                            <div className="tab-header tab-header-spacer"> | </div>
                            <div style={{ cursor: 'pointer' }}
                                className={tab === Tabs.TabHistory ? 'tab-header tab-header-text-select selected' : 'tab-header tab-header-text'}
                                onClick={() => setTabView(Tabs.TabHistory)}>Document history</div>
                            <div className="tab-header tab-header-spacer"> | </div>
                        </div>
                    </ModalSubHeader>
                    {tab === Tabs.TabDetails
                        ? <DocumentDetails document={stateDocument} closeModal={closeModal} editDocument={props.editDocument} />
                        : <DocumentHistoryView document={stateDocument} documentHistory={stateDocumentHistory} onChangeValue={documentHistoryUpdated} />
                    }
                </React.Fragment>
            </Modal>
        );
    }

    return (
        <div className="view-document-modal">
            <ViewInfo />
        </div>
    );
};

type IViewInfoProps = {
    isOpen: boolean,
    onClose: () => void,
    onUpdate: () => void,
    documentId: number
};

export const ViewInfo = (props: IViewInfoProps) => {
    const [showEditDocument, setShowEditDocument] = useState(false);
    const [actionDocumentId, setActionDocumentId] = useState(0);
    const [refresh, setRefresh] = useState({});

    const editDocument = (docId: number | undefined) => {
        if (typeof docId === 'number') {
            setActionDocumentId(docId);
            setShowEditDocument(true);
        }
    }

    const closeEditDocument = () => {
        setShowEditDocument(false);
    }

    const editDocumentUpdated = () => {
        props.onUpdate();
        setRefresh({});
    }

    return (
        <>
            {showEditDocument ?
                <EditForm
                    isOpen={showEditDocument}
                    onUpdate={editDocumentUpdated}
                    onClose={closeEditDocument}
                    documentId={actionDocumentId} />
                :
                <DocumentForm
                    isOpen={props.isOpen}
                    onClose={props.onClose}
                    editDocument={editDocument}
                    documentId={props.documentId}
                    refresh={refresh}
                    tabType={Tabs.TabDetails} />
            }
        </>
    );
}
